import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { getMetadataSchema } from "../metadataSchemas";

type Props = {
  category: string;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** When the row being edited is a system default, lockedOnSystem fields are disabled. */
  systemLocked?: boolean;
};

/**
 * Renders extra editor fields driven by METADATA_SCHEMAS for the given dropdown
 * category. When the schema for the category is unknown, renders nothing —
 * keeps the editor generic across all dropdown categories.
 */
export function MetadataFields({
  category,
  value,
  onChange,
  systemLocked = false,
}: Props) {
  const schema = getMetadataSchema(category);
  if (!schema) return null;

  function set(field: string, fieldValue: unknown) {
    const next = { ...value };
    if (fieldValue === "" || fieldValue === null || fieldValue === undefined) {
      delete next[field];
    } else {
      next[field] = fieldValue;
    }
    onChange(next);
  }

  return (
    <div className="space-y-3">
      {Object.entries(schema).map(([field, def]) => {
        const current = value[field];
        const disabled = Boolean(def.lockedOnSystem && systemLocked);
        return (
          <div key={field} className="space-y-1">
            <Label htmlFor={`meta-${field}`}>
              {def.label}
              {def.required && <span className="text-destructive"> *</span>}
            </Label>
            {def.type === "enum" ? (
              <Select
                id={`meta-${field}`}
                value={typeof current === "string" ? current : ""}
                disabled={disabled}
                onChange={(e) => set(field, e.target.value)}
              >
                {!def.required && <option value="">— None —</option>}
                {def.required && !current && <option value="">— Select —</option>}
                {def.values.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                id={`meta-${field}`}
                value={typeof current === "string" ? current : ""}
                disabled={disabled}
                maxLength={def.maxLength}
                onChange={(e) => set(field, e.target.value)}
                placeholder={def.help}
              />
            )}
            {def.help && (
              <p className="text-[11px] text-muted-foreground">{def.help}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
